// Validation utilities
export const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

export const validatePhoneNumber = (phoneNumber) => {
    const phoneRegex = /^[0-9]{10}$/;
    return phoneRegex.test(phoneNumber.replace(/\D/g, ''));
};

export const validateSlug = (slug) => {
    const slugRegex = /^[a-z0-9-]+$/;
    return slugRegex.test(slug) && slug.length > 0;
};

export const validateName = (name) => {
    return name && name.trim().length > 0 && name.trim().length <= 100;
};

export const validateDate = (dateString) => {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date);
};

// Format utilities
export const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    });
};

export const formatDateShort = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    });
};

export const calculateAge = (birthDate, deathDate = null) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const end = deathDate ? new Date(deathDate) : new Date();

    let age = end.getFullYear() - birth.getFullYear();
    const monthDiff = end.getMonth() - birth.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && end.getDate() < birth.getDate())) {
        age--;
    }

    return age >= 0 ? age : null;
};

export const getInitials = (firstName, lastName) => {
    const first = firstName?.charAt(0)?.toUpperCase() || '';
    const last = lastName?.charAt(0)?.toUpperCase() || '';
    return (first + last).substring(0, 2);
};

// String utilities
export const truncateString = (str, length = 50) => {
    if (!str) return '';
    return str.length > length ? str.substring(0, length) + '...' : str;
};

export const capitalizeString = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const slugify = (str) => {
    if (!str) return '';
    return str
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/^-+|-+$/g, '');
};

// Gender utilities
export const getGenderIcon = (gender) => {
    switch (gender?.toLowerCase()) {
        case 'male':
            return '♂️';
        case 'female':
            return '♀️';
        default:
            return '👤';
    }
};

export const getGenderColor = (gender) => {
    switch (gender?.toLowerCase()) {
        case 'male':
            return '#3498db';
        case 'female':
            return '#e74c3c';
        default:
            return '#95a5a6';
    }
};

// Array utilities
export const groupBy = (array, key) => {
    return array.reduce((acc, obj) => {
        const group = obj[key];
        if (!acc[group]) {
            acc[group] = [];
        }
        acc[group].push(obj);
        return acc;
    }, {});
};

export const findById = (array, id) => {
    return array.find((item) => item.id === id);
};

export const filterByProperty = (array, property, value) => {
    return array.filter((item) => item[property] === value);
};

// Error utilities
export const getErrorMessage = (error) => {
    if (typeof error === 'string') {
        return error;
    }
    if (error?.message) {
        return error.message;
    }
    if (error?.data?.message) {
        return error.data.message;
    }
    return 'An unexpected error occurred';
};

// Local storage utilities
export const setLocalStorage = (key, value) => {
    try {
        localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
        console.error('Error setting localStorage:', error);
    }
};

export const getLocalStorage = (key, defaultValue = null) => {
    try {
        const item = localStorage.getItem(key);
        return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
        console.error('Error getting localStorage:', error);
        return defaultValue;
    }
};

export const removeLocalStorage = (key) => {
    try {
        localStorage.removeItem(key);
    } catch (error) {
        console.error('Error removing localStorage:', error);
    }
};

// Google Drive utilities
export const extractGoogleDriveImageId = (url) => {
    // Extract image ID from Google Drive sharing URLs
    const patterns = [
        /\/d\/([a-zA-Z0-9-_]+)/,
        /id=([a-zA-Z0-9-_]+)/,
        /open\?id=([a-zA-Z0-9-_]+)/,
    ];

    for (const pattern of patterns) {
        const match = url.match(pattern);
        if (match) {
            return match[1];
        }
    }
    return null;
};

export const getGoogleDriveImageUrl = (imageId) => {
    return `https://drive.google.com/uc?export=view&id=${imageId}`;
};

export const isGoogleDriveUrl = (url) => {
    return url?.includes('drive.google.com') || url?.includes('docs.google.com');
};
